import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Database, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { ForgeAnalyticsModel } from "@/frontend/features/Fde/data/types";

type Props = {
  models: ForgeAnalyticsModel[];
  selected?: ForgeAnalyticsModel;
  onSelect: (name: string) => void;
  copy: Record<string, string>;
};

function layerLabel(name: string) {
  return name.split("_", 1)[0]?.toUpperCase() || "DATA";
}

export default function DatasetPicker({ models, selected, onSelect, copy }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showTechnical, setShowTechnical] = useState(false);
  const filtered = useMemo(() => models.filter((model) => {
    if (!showTechnical && model.visibility === "technical" && model.name !== selected?.name) return false;
    const searchable = `${model.label ?? ""} ${model.name} ${model.description ?? ""}`.toLowerCase();
    return searchable.includes(query.trim().toLowerCase());
  }), [models, query, selected?.name, showTechnical]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex min-w-0 max-w-xl flex-1 items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left shadow-xs transition-colors hover:bg-muted/30"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/8 text-primary">
            <Database className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{copy.dataset}</span>
            <span className="block truncate text-sm font-semibold">{selected?.label || selected?.name}</span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(620px,calc(100vw-32px))] p-0">
        <div className="border-b border-border p-3">
          <label className="relative block">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={copy.search_models}
              aria-label={copy.search_models}
              className="h-9 border-0 bg-muted/45 pl-9 shadow-none focus-visible:ring-1"
            />
          </label>
          <div className="mt-2 flex rounded-md bg-muted/45 p-0.5 text-xs">
            <button type="button" onClick={() => setShowTechnical(false)} className={cn("flex-1 rounded px-2 py-1.5", !showTechnical && "bg-background font-medium shadow-xs")}>{copy.business_models}</button>
            <button type="button" onClick={() => setShowTechnical(true)} className={cn("flex-1 rounded px-2 py-1.5", showTechnical && "bg-background font-medium shadow-xs")}>{copy.technical_models}</button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {filtered.map((model) => (
            <button
              key={model.name}
              type="button"
              onClick={() => {
                onSelect(model.name);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-muted/60",
                model.name === selected?.name && "bg-primary/6",
              )}
            >
              <span className="w-10 shrink-0 text-[9px] font-bold tracking-wider text-muted-foreground">{layerLabel(model.name)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{model.label || model.name}</span>
                <span className="block truncate font-mono text-[10px] text-muted-foreground">{model.name}</span>
              </span>
              <span className="shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
                {model.row_count != null && <span className="block">{model.row_count.toLocaleString("pt-BR")} {copy.rows}</span>}
                <span>{model.fields.length} {copy.fields}</span>
              </span>
              <Check className={cn("size-3.5 text-primary", model.name !== selected?.name && "opacity-0")} />
            </button>
          ))}
          {filtered.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">{copy.no_dataset_match}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
